/**
 * Turkish formatting helpers — finans module.
 *
 * Deliberately duplicated per feature (same rule as satinalma/dashboard): a
 * shared format module would couple every screen's rounding decisions together.
 */

const amountFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Whole-lira form for the ladder labels — cheque amounts are round numbers. */
const wholeFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })

const compactFormat = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 2,
})

/** Korgun currency code → symbol. P_DOV_Tip: EU=EURO, TL=Turkish Lira, US=Dollar. */
const CURRENCY_SYMBOL: Record<string, string> = {
  TL: '₺',
  EU: '€',
  US: '$',
}

export function currencySymbol(code: string | null): string {
  if (!code) return ''
  return CURRENCY_SYMBOL[code] ?? code
}

export function formatAmount(value: number | null | undefined, code: string | null = null): string {
  if (value === null || value === undefined) return '—'
  const symbol = currencySymbol(code)
  return symbol ? `${amountFormat.format(value)} ${symbol}` : amountFormat.format(value)
}

export function formatWholeAmount(value: number, code: string | null = null): string {
  const symbol = currencySymbol(code)
  return symbol ? `${wholeFormat.format(value)} ${symbol}` : wholeFormat.format(value)
}

/** '2,84 Mn ₺' — for the ladder bars, where the full figure would collide. */
export function formatCompactAmount(value: number, code: string | null = null): string {
  const symbol = currencySymbol(code)
  return symbol ? `${compactFormat.format(value)} ${symbol}` : compactFormat.format(value)
}

/** API dates are 'YYYY-MM-DD' text; converted to dd.mm.yyyy without going through Date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return iso
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD HH:MM' → 'dd.mm.yyyy HH:MM'. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const [datePart, time] = value.split(' ')
  const d = formatDate(datePart ?? null)
  return time ? `${d} ${time}` : d
}

const MONTH_SHORT = [
  'Oca',
  'Şub',
  'Mar',
  'Nis',
  'May',
  'Haz',
  'Tem',
  'Ağu',
  'Eyl',
  'Eki',
  'Kas',
  'Ara',
] as const

/** 'YYYY-MM' → 'Ağu 26' — pure string math, no Date. */
export function formatMonth(month: string): string {
  const [year, m] = month.split('-')
  const index = Number(m) - 1
  const label = MONTH_SHORT[index]
  if (!year || !label) return month
  return `${label} ${year.slice(2)}`
}
